/**
 * Helpers compartidos para construir el input de "deshacer match" (unmatch).
 *
 * Se extraen desde MatchRow.handleDeleteMatch para que la lógica de un match
 * individual y la de "deshacer todos los matchs" (bulk) sea EXACTAMENTE la misma
 * y no pueda divergir. Reciben los mismos objetos `match` enriquecidos que
 * recibe MatchRow.
 */

export const normalizeUnmatchId = (value) => {
  if (!value) return value;
  const str = String(value);
  // Algunas listas sufijan los ids con "-0" para desambiguar filas; el backend
  // espera el id crudo.
  return str.endsWith("-0") ? str.replace(/-0$/, "") : str;
};

export const getUnmatchEndType = (match) => {
  // IMPORTANTE: si existe docId es un match de transacción (MatchTransaction).
  if (match?.docId) return "TRANSACTION";

  // Para matches de documento, el backend espera el documentType (OBLIGATION/TAX_DOCUMENT).
  if (match?.documentType) return match.documentType;

  // Para transacciones puras, normalizar SPEND/RECEIVED/GIVEN a TRANSACTION.
  const transactionType = match?.type || "TRANSACTION";
  if (["SPEND", "RECEIVED", "RECIEVED", "GIVEN"].includes(transactionType)) {
    return "TRANSACTION";
  }
  return transactionType;
};

/**
 * Construye el objeto de input para generateMultipleUnMatch a partir de un match.
 * Devuelve null si no se pueden resolver los ids (mismo criterio que MatchRow).
 */
export const buildUnmatchInput = (match, editData, isDocument, companyId) => {
  const originId = normalizeUnmatchId(
    isDocument ? editData?._id || editData?.id : editData?.id || editData?._id,
  );
  const endId = normalizeUnmatchId(match?.id || match?.docId || match?._id);

  if (!originId || !endId) return null;

  // Resolver matchId (para borrado de matches parciales): directo o buscando en
  // la lista de matchs del registro.
  let matchId = match.matchId || match.match_id || null;
  if (!matchId) {
    const matchs = isDocument
      ? editData?.meta?.matchs?.inMatch || []
      : editData?.matchs || [];
    const matchKey = match.matchId || match.id || match.docId || match.settleId;
    const foundMatch = matchs.find(
      (m) =>
        String(m.matchId || "") === String(matchKey) ||
        String(m.id) === String(matchKey) ||
        String(m.docId) === String(matchKey) ||
        String(m.settleId) === String(matchKey),
    );
    if (foundMatch?.matchId) matchId = foundMatch.matchId;
  }

  return {
    originType: isDocument ? editData.documentType || "TAX_DOCUMENT" : "TRANSACTION",
    endType: getUnmatchEndType(match),
    originId,
    endId,
    companyId,
    ...(matchId && { matchId }),
  };
};

const DEFAULT_UNMATCH_ERROR =
  "Hubo un problema al deshacer el match del documento.";

/**
 * Extrae el mensaje real del backend (p. ej. período contable cerrado) para el toast.
 * Urql CombinedError suele traerlo en graphQLErrors[0].message.
 */
export const getUnmatchErrorAlert = (error, fallback = DEFAULT_UNMATCH_ERROR) => {
  const graphQLMessage =
    error?.graphQLErrors?.[0]?.message ||
    error?.networkError?.result?.errors?.[0]?.message;
  const rawMessage = graphQLMessage || error?.message || fallback;
  const errorMessage =
    String(rawMessage)
      .replace(/^\[GraphQL\]\s*/i, "")
      .trim() || fallback;
  const normalized = errorMessage.toLowerCase();
  const isPeriodLocked =
    (normalized.includes("período") || normalized.includes("periodo")) &&
    normalized.includes("cerrado");

  return {
    content: errorMessage,
    concept: isPeriodLocked ? "Período Cerrado" : undefined,
    state: isPeriodLocked ? "warning" : "error",
    duration: isPeriodLocked ? 6 : 4,
  };
};
